import React, { useContext } from 'react';
import { OrderTransaction } from '../lookups';
import { Box, Typography, styled, useTheme } from '@mui/material';
import { formatDate, formatMoney, generateNamesObject } from '../../../utils';
import { Chip } from '../../Chip';
import { TradingFormContext } from '../TradingFormContext';

export const optionLegQuoteClasses = generateNamesObject(['root', 'container'], 'OptionLegQuote');

const Container = styled(Box)(() => ({
    [`& .${optionLegQuoteClasses.container}`]: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
    },
}));

export type OptionLeg = {
    call: boolean;
    strikePrice: number;
    expirationDate: string;
    lastPrice: number;
    bid: number;
    ask: number;
    symbol: string;
    volume: number;
    underlyingSymbol?: string;
    askSize?: number;
    bidSize?: number;
    action?: OrderTransaction;
    dayHigh?: number;
    dayLow?: number;
    currency?: string;
};

export type OptionLegQuoteProps = {
    quote: OptionLeg;
    renderActionChip?: boolean;
};

export const OptionLegQuote: React.FunctionComponent<OptionLegQuoteProps> = ({ quote, renderActionChip = true }) => {
    const theme = useTheme();
    const { translate } = useContext(TradingFormContext);
    const buyLeg = quote.action === OrderTransaction.Buy;
    const callOrPutBackgroundColor = quote.call ? theme.palette.primary.main : theme.palette.error.main;
    const actionBackgroundColor = buyLeg ? theme.palette.success.main : theme.palette.error.main;
    const currency = quote.currency;

    return (
        <Container className={optionLegQuoteClasses.root}>
            <Box className={optionLegQuoteClasses.container}>
                {quote.action && renderActionChip && (
                    <Chip
                        style={{
                            backgroundColor: actionBackgroundColor,
                        }}
                    >
                        {buyLeg ? translate('optionSelect.options.buy') : translate('optionSelect.options.sell')}
                    </Chip>
                )}
                {formatMoney(quote.strikePrice, { currency })}
                <Chip
                    style={{
                        backgroundColor: callOrPutBackgroundColor,
                    }}
                >
                    {quote.call ? translate('optionSelect.call') : translate('optionSelect.put')}
                </Chip>
                {quote.expirationDate && formatDate(quote.expirationDate)}
            </Box>
            <Typography variant="caption">
                {formatMoney(quote.lastPrice, { currency })}&nbsp;&nbsp;&nbsp;&nbsp;
                {formatMoney(quote.bid, { currency })} / {formatMoney(quote.ask, { currency })}
            </Typography>
        </Container>
    );
};
